import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  Layout,
  Database,
  Upload,
  Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';
import { showSuccessToast, showErrorToast } from '../lib/toast';
import { useSampleTemplateThumbnailSrc } from '../hooks/useSampleTemplateThumbnailSrc';
import { useWorkspaceCardActions } from '../hooks/useWorkspaceCardActions';
import { workspaceApi } from '../api/workspaceApi';
import CreateWorkspaceWizard from '../components/workspace/CreateWorkspaceWizard';
import SampleWorkspaceTemplateModal from '../components/workspace/SampleWorkspaceTemplateModal';
import WorkspaceCard, { WORKSPACE_CARD_GRID } from '../components/workspace/WorkspaceCard';
import WorkspaceSummaryModal from '../components/workspace/WorkspaceSummaryModal';

function hashStringToNonNegativeInt(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

/** Deterministic gradients + title chrome when a sample has no image thumbnail. */
const SAMPLE_TEMPLATE_FALLBACK_PALETTES = [
  {
    bgClass:
      'bg-gradient-to-br from-cyan-300 via-sky-500 to-indigo-700 dark:from-slate-950 dark:via-sky-950 dark:to-indigo-950',
    titleGradient: 'linear-gradient(168deg, #f0fdfa 0%, #7dd3fc 38%, #1e3a8a 92%)'
  },
  {
    bgClass:
      'bg-gradient-to-br from-amber-300 via-orange-500 to-rose-700 dark:from-stone-950 dark:via-orange-950 dark:to-rose-950',
    titleGradient: 'linear-gradient(168deg, #fffbeb 0%, #fb923c 40%, #9f1239 92%)'
  },
  {
    bgClass:
      'bg-gradient-to-br from-violet-300 via-fuchsia-500 to-pink-700 dark:from-violet-950 dark:via-fuchsia-950 dark:to-pink-950',
    titleGradient: 'linear-gradient(168deg, #faf5ff 0%, #e879f9 42%, #86198f 92%)'
  },
  {
    bgClass:
      'bg-gradient-to-br from-lime-300 via-emerald-500 to-teal-800 dark:from-lime-950 dark:via-emerald-950 dark:to-teal-950',
    titleGradient: 'linear-gradient(168deg, #ecfccb 0%, #34d399 40%, #115e59 92%)'
  },
  {
    bgClass:
      'bg-gradient-to-br from-red-300 via-orange-400 to-yellow-500 dark:from-red-950 dark:via-orange-950 dark:to-yellow-950',
    titleGradient: 'linear-gradient(168deg, #fef2f2 0%, #fb7185 38%, #a16207 90%)'
  },
  {
    bgClass:
      'bg-gradient-to-br from-indigo-300 via-purple-500 to-slate-800 dark:from-indigo-950 dark:via-purple-950 dark:to-slate-950',
    titleGradient: 'linear-gradient(168deg, #eef2ff 0%, #a78bfa 40%, #312e81 92%)'
  },
  {
    bgClass:
      'bg-gradient-to-br from-sky-200 via-cyan-400 to-blue-700 dark:from-slate-900 dark:via-cyan-950 dark:to-blue-950',
    titleGradient: 'linear-gradient(168deg, #ecfeff 0%, #22d3ee 38%, #1e40af 92%)'
  },
  {
    bgClass:
      'bg-gradient-to-br from-fuchsia-300 via-purple-600 to-indigo-900 dark:from-fuchsia-950 dark:via-purple-950 dark:to-indigo-950',
    titleGradient: 'linear-gradient(168deg, #fdf4ff 0%, #c084fc 40%, #312e81 92%)'
  }
];

/** Light metallic wordart readable on slightly dampened photos. */
const SAMPLE_WORDART_ON_THUMB_STYLE = {
  backgroundImage:
    'linear-gradient(168deg, #ffffff 0%, #f8fafc 35%, #cbd5e1 55%, #64748b 92%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  filter:
    'drop-shadow(0 0 2px rgba(0,0,0,0.9)) drop-shadow(0 2px 0 rgba(0,0,0,0.85)) drop-shadow(0 4px 10px rgba(0,0,0,0.5)) drop-shadow(0 0 14px rgba(255,255,255,0.2))'
};

function paletteWordArtTextStyle(titleGradient) {
  return {
    backgroundImage: titleGradient,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    filter:
      'drop-shadow(0 1px 0 rgba(255,255,255,0.55)) drop-shadow(0 2px 0 rgba(0,0,0,0.35)) drop-shadow(0 4px 12px rgba(0,0,0,0.35))'
  };
}

const SampleWorkspaceTemplateCard = ({
  filename,
  name,
  description,
  thumbnail,
  dataset_count: datasetCount,
  udf_count: udfCount,
  onOpen,
  isCreating
}) => {
  const { displaySrc: thumbSrc, loadFailed, setLoadFailedFromImg } =
    useSampleTemplateThumbnailSrc(thumbnail);
  const showThumbnail = Boolean(thumbSrc) && !loadFailed;
  const palette =
    SAMPLE_TEMPLATE_FALLBACK_PALETTES[
      hashStringToNonNegativeInt(filename) % SAMPLE_TEMPLATE_FALLBACK_PALETTES.length
    ];

  const activate = () => {
    if (!isCreating) onOpen({ filename, name, description, thumbnail, dataset_count: datasetCount, udf_count: udfCount });
  };

  const handleCardKeyDown = (e) => {
    if (isCreating) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate();
    }
  };

  return (
    <div
      role="button"
      tabIndex={isCreating ? -1 : 0}
      onClick={activate}
      onKeyDown={handleCardKeyDown}
      aria-label={
        isCreating
          ? `Creating workspace from sample ${name}`
          : `View details for sample workspace ${name}`
      }
      aria-busy={isCreating}
      className={cn(
        'group flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white text-left transition-all duration-300 dark:border-gray-800 dark:bg-gray-900',
        'hover:border-amber-500/40 hover:shadow-lg hover:shadow-amber-500/5 dark:hover:border-amber-500/40',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-50 dark:focus-visible:ring-offset-gray-950',
        isCreating ? 'cursor-wait opacity-90' : 'cursor-pointer'
      )}
    >
      <div className="relative aspect-[5/3] w-full shrink-0 bg-gray-100 dark:bg-gray-800">
        {showThumbnail ? (
          <>
            <img
              src={thumbSrc}
              alt=""
              className="absolute inset-0 h-full w-full object-cover saturate-[0.82] brightness-[0.92] contrast-[0.96] dark:saturate-[0.78] dark:brightness-[0.88]"
              loading="lazy"
              decoding="async"
              onError={() => setLoadFailedFromImg()}
            />
            <div
              className="pointer-events-none absolute inset-0 bg-black/15 dark:bg-black/25"
              aria-hidden
            />
          </>
        ) : (
          <div className={cn('absolute inset-0', palette.bgClass)} />
        )}
        <div
          className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center px-3 pb-2 pt-9"
          aria-hidden
        >
          <p
            className="line-clamp-3 max-h-full w-full break-words text-center text-[clamp(0.68rem,0.55rem+1.4vw,1.05rem)] font-black uppercase italic leading-[1.05] tracking-tight [transform:skewX(-6deg)_scaleX(0.96)]"
            style={showThumbnail ? SAMPLE_WORDART_ON_THUMB_STYLE : paletteWordArtTextStyle(palette.titleGradient)}
          >
            {name}
          </p>
        </div>
        <div className="absolute left-2.5 top-2.5 z-10 rounded-lg bg-black/40 p-1.5 text-amber-200 backdrop-blur-sm dark:bg-black/50">
          <Sparkles size={16} strokeWidth={2.25} />
        </div>
        <span
          className="pointer-events-none absolute right-2.5 top-2.5 z-10 max-w-[min(100%,11rem)] rounded-lg bg-emerald-600 px-2.5 py-1.5 text-center font-black text-[9px] uppercase leading-tight tracking-widest text-white shadow-lg shadow-emerald-600/20 transition-colors group-hover:bg-emerald-700 dark:bg-emerald-500 dark:text-emerald-950 dark:shadow-emerald-950/30 dark:group-hover:bg-emerald-400"
          aria-hidden
        >
          {isCreating ? 'Starting…' : 'View details'}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-3.5">
        <h3 className="sr-only mb-0 font-black uppercase tracking-tight">{name}</h3>
        <p className="mb-2 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
          {description || 'No description in template.'}
        </p>
        <div className="mt-auto flex items-center gap-2.5 border-t border-gray-100 pt-2 text-[9px] font-black uppercase tracking-widest text-gray-500 dark:border-gray-800">
          <span className="flex items-center gap-1">
            <Database size={11} />
            {datasetCount} datasets
          </span>
          <span className="flex items-center gap-1 border-l border-gray-200 pl-2.5 dark:border-gray-700">
            {udfCount} UDFs
          </span>
        </div>
      </div>
    </div>
  );
};

function parseWorkspacesListFilter(searchParams) {
  if (searchParams.get('tab') === 'samples') return 'samples';
  const f = searchParams.get('filter');
  if (f === 'all' || f === 'mine' || f === 'samples') return f;
  return 'all';
}

const WORKSPACES_FILTER_CHIPS = [
  { id: 'all', label: 'All' },
  { id: 'mine', label: 'My workspaces', icon: Layout },
  { id: 'samples', label: 'Samples', icon: Sparkles }
];

/** Heading, search hint, and ordered sections for each list filter. */
const LIST_FILTER_PAGE = {
  all: {
    heading: 'Browse',
    placeholder: 'Search workspaces and samples…',
    sections: [
      { kind: 'workspaces', title: 'Your workspaces', denseEmpty: true },
      { kind: 'templates', title: 'Sample workspaces', denseEmpty: true }
    ]
  },
  mine: {
    heading: 'Your workspaces',
    placeholder: 'Search workspaces…',
    sections: [{ kind: 'workspaces' }]
  },
  samples: {
    heading: 'Sample workspaces',
    placeholder: 'Search templates…',
    sections: [{ kind: 'templates' }]
  }
};

const EMPTY_WRAP = 'col-span-full rounded-[40px] border-2 border-dashed border-gray-200 text-center dark:border-gray-800';

const WorkspacesPage = () => {
  useEffect(() => {
    document.title = 'manteo.ai | Workspaces';
  }, []);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [workspaces, setWorkspaces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const {
    selectedWorkspace,
    setSelectedWorkspace,
    exportingWorkspaceId,
    canEditWorkspaceJson,
    handleDeleteWorkspace,
    handleUpdateWorkspace,
    handleExportWorkspaceJson,
    handleOpenWorkspace,
    handlePreviewWorkspace,
    getWorkspaceLocked,
  } = useWorkspaceCardActions(workspaces, setWorkspaces);
  const [importingJson, setImportingJson] = useState(false);
  const importFileInputRef = useRef(null);
  const listFilter = parseWorkspacesListFilter(searchParams);
  const applyListFilter = f => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('tab');
        next.set('filter', f);
        return next;
      },
      { replace: true }
    );
  };

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState(false);
  const [instantiatingFilename, setInstantiatingFilename] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const loadWorkspaces = async () => {
    setIsLoading(true);
    try {
      const data = await workspaceApi.getWorkspaces();
      setWorkspaces(data);
    } catch (err) {
      console.error('Failed to load workspaces:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportJsonFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImportingJson(true);
    try {
      const text = await file.text();
      const doc = JSON.parse(text);
      const newWs = await workspaceApi.importWorkspace(doc);
      showSuccessToast('Workspace imported.');
      setWorkspaces(prev => [newWs, ...prev]);
      navigate(`/workspace/${newWs.id}`);
    } catch (err) {
      console.error('Failed to import workspace:', err);
      if (err instanceof SyntaxError) {
        showErrorToast('Invalid JSON file.');
      } else {
        showErrorToast('Could not import workspace. Check the file format.');
      }
    } finally {
      setImportingJson(false);
    }
  };

  useEffect(() => {
    loadWorkspaces();
  }, []);

  useEffect(() => {
    if (listFilter !== 'samples' && listFilter !== 'all') return undefined;
    let cancelled = false;
    setTemplatesLoading(true);
    setTemplatesError(false);
    workspaceApi
      .getWorkspaceTemplates()
      .then(data => {
        if (!cancelled) setTemplates(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Failed to load workspace templates:', err);
        if (!cancelled) {
          setTemplatesError(true);
          setTemplates([]);
        }
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listFilter]);

  const handleUseTemplate = async (filename) => {
    setInstantiatingFilename(filename);
    try {
      const newWs = await workspaceApi.instantiateWorkspaceTemplate(filename);
      showSuccessToast('Workspace created from template.');
      setWorkspaces(prev => [newWs, ...prev]);
      setSelectedTemplate(null);
      navigate(`/workspace/${newWs.id}`);
    } catch (err) {
      console.error('Failed to instantiate template:', err);
      showErrorToast('Could not create workspace from this template.');
    } finally {
      setInstantiatingFilename(null);
    }
  };

  const filteredWorkspaces = workspaces
    .filter(ws =>
      ws.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ws.description || '').toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const aTs = new Date(a.created_at || a.createdAt || 0).getTime();
      const bTs = new Date(b.created_at || b.createdAt || 0).getTime();
      return bTs - aTs;
    });

  const filteredTemplates = templates.filter(
    t =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.filename || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const page = LIST_FILTER_PAGE[listFilter];

  const workspaceGrid = denseEmpty => (
    <div className={WORKSPACE_CARD_GRID}>
      {isLoading ? (
        [1, 2, 3, 4, 5, 6, 7, 8].map(i => (
          <div key={i} className="h-48 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-900" />
        ))
      ) : filteredWorkspaces.length > 0 ? (
        filteredWorkspaces.map(w => (
          <WorkspaceCard
            key={w.id}
            {...w}
            createdAt={w.created_at || w.createdAt}
            onPreview={handlePreviewWorkspace}
            onOpenWorkspace={handleOpenWorkspace}
            onDelete={handleDeleteWorkspace}
            showExportJson={canEditWorkspaceJson}
            onExportJson={handleExportWorkspaceJson}
            exportJsonLoading={exportingWorkspaceId === w.id}
            isLocked={getWorkspaceLocked(w)}
          />
        ))
      ) : workspaces.length === 0 ? (
        <div className="col-span-full rounded-[28px] border-2 border-dashed border-amber-200/80 bg-amber-50/30 px-6 py-10 text-center dark:border-amber-900/40 dark:bg-amber-950/20">
          <p className="mb-2 text-sm font-black uppercase tracking-tight text-gray-900 dark:text-white">
            No workspaces yet
          </p>
          <p className="mx-auto mb-4 max-w-md text-sm text-gray-500 dark:text-gray-400">
            Get started with a curated sample workspace, or create a new workspace with your own datasets.
          </p>
          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => applyListFilter('samples')}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-6 py-3 font-black text-[10px] uppercase tracking-widest text-white shadow-lg shadow-emerald-600/10 transition-all hover:bg-emerald-700 active:scale-[0.98]"
            >
              <Sparkles size={16} strokeWidth={2.5} />
              Browse sample workspaces
            </button>
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-300/80 bg-white px-6 py-3 font-black text-[10px] uppercase tracking-widest text-emerald-700 transition-all hover:border-emerald-400 hover:bg-emerald-50 active:scale-[0.98] dark:border-emerald-700/70 dark:bg-emerald-950/20 dark:text-emerald-300 dark:hover:border-emerald-600 dark:hover:bg-emerald-950/30"
            >
              <Plus size={16} strokeWidth={2.5} />
              Create workspace
            </button>
          </div>
        </div>
      ) : (
        <div className={cn(EMPTY_WRAP, 'py-20', denseEmpty && 'py-12')}>
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
            No workspaces match your search
          </p>
        </div>
      )}
    </div>
  );

  const templateGrid = denseEmpty => (
    <div className={WORKSPACE_CARD_GRID}>
      {templatesLoading ? (
        [1, 2, 3, 4].map(i => (
          <div key={i} className="h-44 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-900" />
        ))
      ) : templatesError ? (
        <div className={cn(EMPTY_WRAP, 'border-red-200 py-16 dark:border-red-900/40')}>
          <p className="text-xs font-bold uppercase tracking-widest text-red-600 dark:text-red-400">
            Could not load sample workspaces
          </p>
        </div>
      ) : filteredTemplates.length > 0 ? (
        filteredTemplates.map(t => (
          <SampleWorkspaceTemplateCard
            key={t.filename}
            {...t}
            onOpen={setSelectedTemplate}
            isCreating={instantiatingFilename === t.filename}
          />
        ))
      ) : (
        <div className={cn(EMPTY_WRAP, 'py-20', denseEmpty && 'py-10')}>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-400">
            {templates.length === 0 ? 'No sample workspaces found' : 'No samples match your search'}
          </p>
          {templates.length === 0 ? (
            <p
              className={cn(
                'mx-auto max-w-md text-gray-500',
                denseEmpty ? 'mt-2 text-xs dark:text-gray-400' : 'text-sm'
              )}
            >
              Add JSON export files under{' '}
              <code
                className={cn(
                  'rounded bg-gray-100 dark:bg-gray-800',
                  denseEmpty ? 'px-1 py-0.5 font-mono text-[10px]' : 'px-1.5 py-0.5 text-xs'
                )}
              >
                backend/data/workspace_templates
              </code>
              {!denseEmpty ? (
                <>
                  {' '}
                  (see export format <code className="text-xs">manteo_workspace_export</code>).
                </>
              ) : (
                '.'
              )}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50/50 px-8 py-8 transition-colors duration-300 dark:bg-gray-950">
      <CreateWorkspaceWizard
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={async (workspaceData) => {
          try {
            const newWs = await workspaceApi.createWorkspace(workspaceData);
            showSuccessToast('Workspace created.');
            navigate(`/workspace/${newWs.id}`);
          } catch (err) {
            console.error('Failed to create workspace:', err);
            throw err;
          }
        }}
      />
      <WorkspaceSummaryModal
        workspace={selectedWorkspace}
        isOpen={Boolean(selectedWorkspace)}
        onClose={() => setSelectedWorkspace(null)}
        onOpenWorkspace={handleOpenWorkspace}
        onUpdateWorkspace={handleUpdateWorkspace}
      />
      <SampleWorkspaceTemplateModal
        templateSummary={selectedTemplate}
        isOpen={Boolean(selectedTemplate)}
        onClose={() => setSelectedTemplate(null)}
        onUse={handleUseTemplate}
        isCreating={Boolean(
          selectedTemplate && instantiatingFilename === selectedTemplate.filename
        )}
      />

      <div className="mx-auto max-w-7xl space-y-5">
        <header className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <h1 className="text-3xl font-black uppercase tracking-tight text-gray-900 dark:text-white">Workspaces</h1>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">Connect different data sources and create workspaces to analyze your data.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            {canEditWorkspaceJson && (
              <>
                <input
                  ref={importFileInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={handleImportJsonFile}
                />
                <button
                  type="button"
                  onClick={() => importFileInputRef.current?.click()}
                  disabled={importingJson}
                  className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-sky-500/60 hover:text-sky-700 dark:hover:text-sky-300 transition-all font-black text-[10px] uppercase tracking-widest active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
                >
                  <Upload size={16} strokeWidth={2.5} />
                  {importingJson ? 'Importing…' : 'Import JSON'}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center justify-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-black text-[10px] uppercase tracking-widest shadow-lg shadow-emerald-600/10 active:scale-95"
            >
              <Plus size={16} strokeWidth={3} />
              New Workspace
            </button>
          </div>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div
            aria-label="Workspace list filters"
            className="inline-flex w-fit flex-wrap items-center gap-1 rounded-[22px] border border-gray-200/70 bg-white/70 p-1.5 shadow-inner shadow-gray-200/60 backdrop-blur dark:border-gray-800 dark:bg-gray-900/60 dark:shadow-black/20"
          >
            {WORKSPACES_FILTER_CHIPS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                aria-pressed={listFilter === id}
                onClick={() => applyListFilter(id)}
                className={cn(
                  'group inline-flex items-center gap-2 rounded-2xl px-4 py-2 font-black text-[10px] uppercase tracking-widest transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40',
                  listFilter === id
                    ? 'bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-300 dark:bg-gray-800 dark:text-emerald-300 dark:ring-emerald-700/70'
                    : 'text-gray-500 hover:bg-gray-100/80 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800/80 dark:hover:text-white'
                )}
              >
                {Icon ? <Icon size={14} className="shrink-0" strokeWidth={2.5} /> : null}
                {label}
              </button>
            ))}
          </div>
          <div className="relative group w-full sm:w-[280px]">
            <Search size={14} strokeWidth={2.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-emerald-500" />
            <input
              type="text"
              placeholder={page.placeholder}
              className="w-full rounded-xl border border-gray-100 bg-white py-2 pl-10 pr-4 text-[10px] font-black tracking-widest shadow-sm transition-all focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-gray-800 dark:bg-gray-900"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <section>
          <div className={cn(page.sections.length > 1 && 'space-y-14')}>
            {page.sections.map((sec, idx) => (
              <div key={`${sec.kind}-${idx}`}>
                {sec.title ? (
                  <h3 className="mb-4 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    {sec.title}
                  </h3>
                ) : null}
                {sec.kind === 'workspaces' ? workspaceGrid(sec.denseEmpty) : templateGrid(sec.denseEmpty)}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default WorkspacesPage;
